import type { SmileSimulationAction } from "./actions";
import type { SmileSimulationState } from "./state";

export function smileSimulationReducer(
  state: SmileSimulationState,
  action: SmileSimulationAction,
): SmileSimulationState {
  switch (action.type) {
    case "step/set":
      if (state.step === action.step) {
        return state;
      }

      return {
        ...state,
        step: action.step,
      };

    case "auth/emailUpdated":
      if (
        state.auth.email === action.email &&
        state.auth.emailError === null &&
        state.auth.submitError === null
      ) {
        return state;
      }

      return {
        ...state,
        auth: {
          ...state.auth,
          email: action.email,
          emailError: null,
          submitError: null,
        },
      };

    case "auth/emailErrorUpdated":
      if (state.auth.emailError === action.message) {
        return state;
      }

      return {
        ...state,
        auth: {
          ...state.auth,
          emailError: action.message,
        },
      };

    case "auth/passwordUpdated":
      if (
        state.auth.password === action.password &&
        state.auth.passwordError === null &&
        state.auth.submitError === null
      ) {
        return state;
      }

      return {
        ...state,
        auth: {
          ...state.auth,
          password: action.password,
          passwordError: null,
          submitError: null,
        },
      };

    case "auth/passwordErrorUpdated":
      if (state.auth.passwordError === action.message) {
        return state;
      }

      return {
        ...state,
        auth: {
          ...state.auth,
          passwordError: action.message,
        },
      };

    case "auth/submitErrorUpdated":
      if (state.auth.submitError === action.message) {
        return state;
      }

      return {
        ...state,
        auth: {
          ...state.auth,
          submitError: action.message,
        },
      };

    case "auth/submittingChanged":
      if (state.auth.isSubmitting === action.isSubmitting) {
        return state;
      }

      return {
        ...state,
        auth: {
          ...state.auth,
          isSubmitting: action.isSubmitting,
        },
      };

    case "patientDraft/nameUpdated":
      if (
        state.patientDraft.name === action.name &&
        state.patientDraft.nameError === null
      ) {
        return state;
      }

      return {
        ...state,
        patientDraft: {
          ...state.patientDraft,
          name: action.name,
          nameError: null,
        },
      };

    case "patientDraft/nameErrorUpdated":
      if (state.patientDraft.nameError === action.message) {
        return state;
      }

      return {
        ...state,
        patientDraft: {
          ...state.patientDraft,
          nameError: action.message,
        },
      };

    case "patientDraft/chartNumberUpdated":
      if (
        state.patientDraft.chartNumber === action.chartNumber &&
        state.patientDraft.chartNumberError === null
      ) {
        return state;
      }

      return {
        ...state,
        patientDraft: {
          ...state.patientDraft,
          chartNumber: action.chartNumber,
          chartNumberError: null,
        },
      };

    case "patientDraft/chartNumberErrorUpdated":
      if (state.patientDraft.chartNumberError === action.message) {
        return state;
      }

      return {
        ...state,
        patientDraft: {
          ...state.patientDraft,
          chartNumberError: action.message,
        },
      };

    case "patientDraft/prefilled":
      if (
        state.patientDraft.name === action.name &&
        state.patientDraft.chartNumber === action.chartNumber &&
        state.patientDraft.nameError === null &&
        state.patientDraft.chartNumberError === null
      ) {
        return state;
      }

      return {
        ...state,
        patientDraft: {
          name: action.name,
          nameError: null,
          chartNumber: action.chartNumber,
          chartNumberError: null,
        },
      };

    case "patientDraft/reset":
      if (
        state.patientDraft.name === "" &&
        state.patientDraft.chartNumber === "" &&
        state.patientDraft.nameError === null &&
        state.patientDraft.chartNumberError === null
      ) {
        return state;
      }

      return {
        ...state,
        patientDraft: {
          name: "",
          nameError: null,
          chartNumber: "",
          chartNumberError: null,
        },
      };

    case "session/currentPatientUpdated":
      if (state.currentPatientId === action.patientId) {
        return state;
      }

      return {
        ...state,
        currentPatientId: action.patientId,
      };

    case "session/patientUpserted": {
      const patientIndex = state.patients.findIndex(
        (patient) => patient.id === action.patient.id,
      );

      if (patientIndex < 0) {
        return {
          ...state,
          patients: [...state.patients, action.patient],
        };
      }

      const nextPatients = [...state.patients];
      nextPatients[patientIndex] = action.patient;

      return {
        ...state,
        patients: nextPatients,
      };
    }

    case "session/recordAppended":
      return {
        ...state,
        records: [action.record, ...state.records],
      };

    case "history/queryUpdated":
      if (state.history.query === action.query) {
        return state;
      }

      return {
        ...state,
        history: {
          ...state.history,
          query: action.query,
        },
      };

    case "history/patientSelected":
      if (state.history.selectedPatientId === action.patientId) {
        return state;
      }

      return {
        ...state,
        history: {
          ...state.history,
          selectedPatientId: action.patientId,
          selectedRecordId:
            action.patientId == null ? null : state.history.selectedRecordId,
        },
      };

    case "history/recordSelected":
      if (state.history.selectedRecordId === action.recordId) {
        return state;
      }

      return {
        ...state,
        history: {
          ...state.history,
          selectedRecordId: action.recordId,
        },
      };

    case "camera/requested":
      if (
        state.camera.status === "requesting" &&
        state.camera.message === action.message
      ) {
        return state;
      }

      return {
        ...state,
        camera: {
          ...state.camera,
          status: "requesting",
          message: action.message,
        },
      };

    case "camera/ready":
      if (state.camera.status === "ready" && state.camera.message === "") {
        return state;
      }

      return {
        ...state,
        camera: {
          ...state.camera,
          status: "ready",
          message: "",
        },
      };

    case "camera/failed":
      if (
        state.camera.status === "error" &&
        state.camera.message === action.message
      ) {
        return state;
      }

      return {
        ...state,
        camera: {
          ...state.camera,
          status: "error",
          message: action.message,
        },
      };

    case "camera/frameSizeUpdated":
      if (
        state.camera.frameSize &&
        Math.abs(state.camera.frameSize.width - action.frameSize.width) < 0.5 &&
        Math.abs(state.camera.frameSize.height - action.frameSize.height) < 0.5
      ) {
        return state;
      }

      return {
        ...state,
        camera: {
          ...state.camera,
          frameSize: action.frameSize,
        },
      };

    case "capture/setImage":
      if (state.capture.image === action.image) {
        return state;
      }

      return {
        ...state,
        capture: {
          image: action.image,
        },
      };

    case "simulation/reset":
      if (
        state.simulation.status === "idle" &&
        state.simulation.message === "" &&
        state.simulation.resultImage == null &&
        state.simulation.resultRecordId == null &&
        state.simulation.progress === 0 &&
        state.camera.status === "idle" &&
        state.camera.message === "" &&
        state.comparison.value === 50 &&
        !state.comparison.isDragging
      ) {
        return state;
      }

      return {
        ...state,
        simulation: {
          status: "idle",
          message: "",
          resultImage: null,
          resultRecordId: null,
          progress: 0,
        },
        camera: {
          ...state.camera,
          status: "idle",
          message: "",
        },
        comparison: {
          value: 50,
          isDragging: false,
        },
      };

    case "simulation/requested":
      return {
        ...state,
        simulation: {
          status: "processing",
          message: action.message,
          resultImage: null,
          resultRecordId: null,
          progress: 0,
        },
        comparison: {
          value: 50,
          isDragging: false,
        },
      };

    case "simulation/progressUpdated":
      if (state.simulation.progress === action.progress) {
        return state;
      }

      return {
        ...state,
        simulation: {
          ...state.simulation,
          progress: action.progress,
        },
      };

    case "simulation/succeeded":
      return {
        ...state,
        simulation: {
          status: "completed",
          message: action.message,
          resultImage: action.image,
          resultRecordId: action.recordId ?? null,
          progress: 100,
        },
      };

    case "simulation/failed":
      if (
        state.simulation.status === "error" &&
        state.simulation.message === action.message
      ) {
        return state;
      }

      return {
        ...state,
        simulation: {
          ...state.simulation,
          status: "error",
          message: action.message,
        },
      };

    case "settings/promptVersionUpdated":
      if (state.settings.promptVersion === action.promptVersion) {
        return state;
      }

      return {
        ...state,
        settings: {
          ...state.settings,
          promptVersion: action.promptVersion,
        },
      };

    case "settings/languageUpdated":
      if (state.settings.language === action.language) {
        return state;
      }

      return {
        ...state,
        settings: {
          ...state.settings,
          language: action.language,
        },
      };

    case "comparison/valueUpdated":
      if (state.comparison.value === action.value) {
        return state;
      }

      return {
        ...state,
        comparison: {
          ...state.comparison,
          value: action.value,
        },
      };

    case "comparison/draggingChanged":
      if (state.comparison.isDragging === action.isDragging) {
        return state;
      }

      return {
        ...state,
        comparison: {
          ...state.comparison,
          isDragging: action.isDragging,
        },
      };

    default:
      return state;
  }
}
